'use client';

import { Skeleton } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

import { useGetJobNetNetQuery } from '@/features/jobs/api/jobsApi';
import { BillingAccessCTA } from '@/features/subscription/components';
import { getBillingAccessState } from '@/features/subscription/utils/billingAccess';
import { useGetCurrentUserContextQuery } from '@/features/users/api/usersApi';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import {
  Badge,
  DataBadge,
  MainButton,
  ProgressMeter,
  SecondaryButton,
} from '@web/ui';
import { formatHours, formatNumber } from '@web/utils';

import { JobActivateModal } from './JobActivateModal';
import styles from './JobNetNetStep.module.scss';

import type { JobFormMode, JobSummaryDraft } from './types';
import type {
  JobNetNetDeliverableMetric,
  JobNetNetServiceTypeMetric,
} from '@/features/jobs/types';

type JobNetNetStepProps = {
  mode: JobFormMode;
  totalSteps: number;
  summary: JobSummaryDraft;
  isSaving: boolean;
};

function asCurrency(value: string | number | null | undefined) {
  return formatNumber(value ?? 0, { asCurrency: true });
}

function asHours(value: number | null | undefined) {
  return `${formatHours(value)} hrs`;
}

function asHoursPerWeek(value: number | null | undefined) {
  return `${formatHours(value)} hrs/week`;
}

function asDurationLabel(durationDays: number | null | undefined) {
  if (!durationDays || durationDays <= 0) {
    return 'Not set';
  }
  return `${durationDays} days`;
}

function asDurationSummary(durationDays: number | null | undefined) {
  if (!durationDays || durationDays <= 0) {
    return 'an unfixed duration';
  }
  return `a ${durationDays}-day duration`;
}

function buildServiceTypeRevenueLabel(item: JobNetNetServiceTypeMetric) {
  const rate = item.billable_rate ? asCurrency(item.billable_rate) : '$0';
  return `${item.label} (${formatHours(item.assigned_hours)} hrs @ ${rate}/h)`;
}

function ServiceTypeBreakdown({
  data,
  isLoading,
}: {
  data: JobNetNetServiceTypeMetric[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className={styles.jobNetNetStep__breakdownList}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`breakdown-skeleton-${index}`}
            variant="text"
            height={22}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.jobNetNetStep__breakdownList}>
      {data.map((item) => {
        const pct = Math.max(
          Math.min(item.percentage_of_total_hours ?? 0, 100),
          0,
        );
        return (
          <div
            key={item.service_type_id}
            className={styles.jobNetNetStep__breakdownRow}
          >
            <div className={styles.jobNetNetStep__breakdownRowTop}>
              <span className={styles.jobNetNetStep__breakdownLabel}>
                {item.label}
              </span>
              <span className={styles.jobNetNetStep__breakdownPct}>
                {formatHours(item.percentage_of_total_hours)}%
              </span>
            </div>
            <ProgressMeter percentage={pct} size="md" />
          </div>
        );
      })}
    </div>
  );
}

function RevenueByDeliverableTable({
  title,
  rows,
  isLoading,
}: {
  title: string;
  rows: JobNetNetDeliverableMetric[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className={styles.jobNetNetStep__deliverablesTableWrap}>
        <p className={styles.jobNetNetStep__subpanelTitle}>{title}</p>
        <Skeleton variant="rectangular" height={160} />
      </div>
    );
  }

  return (
    <div className={styles.jobNetNetStep__deliverablesTableWrap}>
      <p className={styles.jobNetNetStep__subpanelTitle}>{title}</p>
      <table className={styles.jobNetNetStep__deliverablesTable}>
        <thead>
          <tr>
            <th>Deliverable</th>
            <th className={styles.jobNetNetStep__numericCell}>Hours</th>
            <th className={styles.jobNetNetStep__numericCell}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.deliverable_id}>
              <td>{row.name}</td>
              <td className={styles.jobNetNetStep__numericCell}>
                {asHours(row.total_hours)}
              </td>
              <td className={styles.jobNetNetStep__numericCell}>
                {asCurrency(row.revenue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function JobNetNetStep(props: JobNetNetStepProps) {
  const { totalSteps, summary, isSaving } = props;
  const router = useRouter();
  const workspaceRoutes = useWorkspaceRoutes();
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const {
    data: currentUserContextResponse,
    isLoading: isBillingAccessLoading,
  } = useGetCurrentUserContextQuery();
  const jobId = summary.id ?? '';
  const isProject = summary.jobKind === 'project';
  const canSaveAsPending = summary.status === 'pending';
  const canActivateJob =
    summary.status === 'pending' || summary.status === 'archived';
  const billingAccessState = getBillingAccessState(
    currentUserContextResponse?.data?.workspace?.plan,
  );
  const hasPremiumAccess =
    isBillingAccessLoading ||
    billingAccessState === 'premium_active' ||
    billingAccessState === 'trial_active';

  const { data, isLoading } = useGetJobNetNetQuery(jobId, {
    skip: !summary.id,
    refetchOnMountOrArgChange: true,
  });

  const metrics = data?.data?.metrics;
  const totalHours = metrics?.total_hours ?? 0;
  const durationDays = metrics?.duration_days ?? null;
  const intensity = metrics?.work_intensity_hours_per_week ?? 0;
  const totalBudget = metrics?.total_project_budget ?? '0';
  const serviceTypeBreakdown = metrics?.service_type_breakdown ?? [];
  const revenueByServiceType = metrics?.revenue_by_service_type ?? [];
  const revenueByDeliverable = metrics?.revenue_by_deliverable ?? [];
  const deliverablesCount = revenueByDeliverable.length;

  const summaryLine = useMemo(() => {
    if (isProject) {
      return `Net Net Summary: Balanced. This plan carries ${formatHours(totalHours)} total hours over ${asDurationSummary(durationDays)}, landing at ${asHoursPerWeek(intensity)}.`;
    }
    return `Net Net Summary: Balanced. This retainer carries ${formatHours(totalHours)} monthly hours, landing at ${asHoursPerWeek(intensity)}.`;
  }, [durationDays, intensity, isProject, totalHours]);

  return (
    <section className={styles.jobNetNetStep}>
      <div>
        <Badge
          label={`Step ${totalSteps} of ${totalSteps}`}
          tone="success"
          size="lg"
        />
      </div>
      <div className={styles.jobNetNetStep__header}>
        <h2 className={styles.jobNetNetStep__title}>Net Net</h2>
        <p className={styles.jobNetNetStep__subtitle}>
          Review and approve the plan.
        </p>
      </div>
      <div className={styles.jobNetNetStep__panel}>
        <div className={styles.jobNetNetStep__topGrid}>
          <div className={styles.jobNetNetStep__badgeStack}>
            <DataBadge
              title={isProject ? 'Total project hours' : 'Total monthly hours'}
              value={asHours(totalHours)}
              description={
                isProject
                  ? 'All planned effort from Deliverables + LOE.'
                  : 'All planned effort shown here is monthly.'
              }
              isLoading={isLoading}
            />

            {isProject ? (
              <DataBadge
                title="Duration"
                value={asDurationLabel(durationDays)}
                description="Set dates in Timeline to calculate duration."
                isLoading={isLoading}
              />
            ) : null}

            <DataBadge
              title="Work intensity"
              value={asHoursPerWeek(intensity)}
              description="Balanced."
              isLoading={isLoading}
            />
          </div>

          <div className={styles.jobNetNetStep__breakdown}>
            <h3 className={styles.jobNetNetStep__breakdownTitle}>
              Service Type Breakdown
            </h3>
            <p className={styles.jobNetNetStep__breakdownSubtitle}>
              Hours planned effort is distributed across selected disciplines.
            </p>
            <ServiceTypeBreakdown
              data={serviceTypeBreakdown}
              isLoading={isLoading}
            />
          </div>
        </div>

        <p className={styles.jobNetNetStep__summaryLine}>{summaryLine}</p>

        <div className={styles.jobNetNetStep__budgetPanel}>
          <h3 className={styles.jobNetNetStep__budgetTitle}>
            {isProject
              ? 'Project Budget (Based on Base Rates)'
              : 'Monthly Budget (Based on Base Rates)'}
          </h3>
          <p className={styles.jobNetNetStep__budgetSubtitle}>
            {isProject
              ? 'Price if however you want. Based on your base rates, this is the equivalent project budget.'
              : 'This is your expected monthly budget based on base rates.'}
          </p>

          <div className={styles.jobNetNetStep__budgetGrid}>
            <div className={styles.jobNetNetStep__budgetLeftStack}>
              <DataBadge
                title={
                  isProject ? 'Total project budget' : 'Total monthly budget'
                }
                value={asCurrency(totalBudget)}
                description={
                  isProject
                    ? `Balanced workload based on ${asHoursPerWeek(intensity)}.`
                    : `Balanced workload based on ${asHoursPerWeek(intensity)}.`
                }
                isLoading={isLoading}
              />

              <div className={styles.jobNetNetStep__revenueByType}>
                <p className={styles.jobNetNetStep__subpanelTitle}>
                  {isProject
                    ? 'Revenue by service type'
                    : 'Monthly revenue by service type'}
                </p>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={90} />
                ) : (
                  revenueByServiceType.map((item) => (
                    <div
                      key={item.service_type_id}
                      className={styles.jobNetNetStep__lineItem}
                    >
                      <span>{buildServiceTypeRevenueLabel(item)}</span>
                      <span className={styles.jobNetNetStep__lineValue}>
                        {asCurrency(item.revenue)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <RevenueByDeliverableTable
                title={
                  isProject
                    ? 'Revenue by deliverable'
                    : 'Monthly revenue by deliverable'
                }
                rows={revenueByDeliverable}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
      <footer className={styles.jobNetNetStep__footer}>
        {canSaveAsPending ? (
          <SecondaryButton
            ariaLabel="Save as Pending"
            onClick={() => router.push(workspaceRoutes.jobs.index)}
          >
            Save as Pending
          </SecondaryButton>
        ) : null}
        {canActivateJob && hasPremiumAccess ? (
          <MainButton
            ariaLabel="Activate Job"
            onClick={() => setIsActivateModalOpen(true)}
            disabled={isBillingAccessLoading || isSaving || !summary.id}
            isLoading={isBillingAccessLoading || isSaving}
          >
            Activate Job
          </MainButton>
        ) : null}
        {canActivateJob && !hasPremiumAccess ? (
          <BillingAccessCTA variant="button" reason="activate-job" />
        ) : null}
      </footer>

      <JobActivateModal
        open={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        summary={summary}
        totalHours={totalHours}
        durationDays={durationDays}
        deliverablesCount={deliverablesCount}
      />
    </section>
  );
}
